import * as React from 'react';
import { Icon } from '@app/Components/Icon';
import { observer } from 'mobx-react';
import { observable, action, computed } from 'mobx';

import { Configurations } from '@app/AppConstants/Configurations';
import { ApiUrls } from '@app/AppConstants/ApiUrls';
import { applyDecorators, EmptyProps } from "@app/Helpers/Decorator";
import { ReleaseNoteDtoShort } from '@app/Models/WebApiModels';
import ApiService from '@app/Services/ApiService';
import { configurationStore } from '@app/Stores/ConfigurationStore';

import { modalService } from './Modal';
import { AzureAuth, UserPermissions } from './AzureAuth';
const ReleaseNotesDialog = React.lazy(() => import('@app/Components/Dialogs/ReleaseNotesDialog'));

@observer
export class HelpLink extends React.Component {
    @observable private _helpLink: string = '';
    @observable private _releaseNotes: ReleaseNoteDtoShort[] = [];
    @observable private _isPopupVisible: boolean = false;
    private _timer: number | null = null;

    constructor (props:EmptyProps) {
        super(props);
        applyDecorators(this);
        void configurationStore.get(Configurations.globalGroup, Configurations.helpLinkKey).then((response) => (this._helpLink = response || ''));
    }

    componentDidMount(): void {
        void this._initData();
    }

    public render() {
        const hasReleaseNotesPermissions = AzureAuth.hasAccess(UserPermissions.ReleaseNotesRead);
        if (!hasReleaseNotesPermissions) {
            return (
                <div className="help-link">
                    <a title="Help document" target="_blank" href={this._helpLink} rel="noreferrer">
                        <Icon name="question-circle" className="help-link-icon" />
                    </a>
                </div>
            );
        }

        return (
            <div className="help-link" onClick={this._openReleaseNotes} onMouseOver={this._onMouseOverHandler} onMouseOut={this._hidePopup}>
                <Icon name="question-circle" className="help-link-icon" />
                {this._unreadNotesCount > 0 && (
                    <>
                        <div className="unread-message-indicator" />
                        {this._isPopupVisible && (
                            <div className="unread-message-popup">
                                <Icon name="comment" className="mr-1" />
                                {this._unreadNotesCount} unread release notes
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }

    @action.bound
    private async _openReleaseNotes() {
        await modalService.show(ReleaseNotesDialog, {
            helpLink: this._helpLink,
            refreshNotesCounter: this._loadReleaseNotes
        });
    }

    private async _initData() {
        const hasReleaseNotesPermissions = AzureAuth.hasAccess(UserPermissions.ReleaseNotesRead);
        if (!hasReleaseNotesPermissions) return;

        await this._loadReleaseNotes();
        this._initPopupTimeout();
    }

    @action.bound
    private async _loadReleaseNotes() {
        const { data } = await ApiService.get<ReleaseNoteDtoShort[]>(ApiUrls.ReleaseNotesUrl, null);
        this._releaseNotes = data;
    }

    @action.bound
    private _onMouseOverHandler() {
        this._stopTimer();
        this._showPopup();
    }

    private _initPopupTimeout() {
        if (!this._unreadNotesCount) return;

        this._timer = window.setTimeout(() => {
            this._showPopup();
            setTimeout(this._hidePopup, 8000);
        }, 5000);
    }

    private _stopTimer() {
        if (this._timer) {
            window.clearTimeout(this._timer);
            this._timer = null;
        }
    }

    @action.bound
    private _showPopup() {
        this._isPopupVisible = true;
    }

    @action.bound
    private _hidePopup() {
        this._isPopupVisible = false;
    }

    @computed
    private get _unreadNotesCount() {
        return this._releaseNotes.filter(n => !n.isDraft && n.isNew).length;
    }
}
